import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Checkbox, TableBody, TableContainer, TableHead, TableRow, Alert } from '@mui/material';
import { AWB, Carrier, Shipment } from '../types';
import { addSpaceAfterComma } from '../DebitNote/type';
import { SBTable } from '@Components/Table/Table';
import { TableHeadCell, TableRowCell } from '@Components/Table/TableCell';
import { SBTableRow } from '@Components/Table/TableRow';
import FilterSearch from '@Components/Filter/Search';
import DateRangePicker from '@Components/DateRangePicker';
import { initialDateRange, SearchFilterList, ShipmentTypeList, TableActionContainer } from './constant';
import FilterSelect from '@Components/Filter/Dropdown';
import SbButton from '@Components/Button';
import { useSelector } from 'react-redux';
import { RootStateType } from '@Store';
import Image from '@Components/Image';
import { useNavigate } from 'react-router-dom';

export interface ServiceGroup {
  id: string;
  name: string;
  awbs: AWB[];
}

const transformServices = (shipments: Shipment[], selectedCarrier: Carrier): ServiceGroup[] => {
  const services: ServiceGroup[] = [];

  shipments.forEach(shipment => {
    if (shipment.carrierId !== selectedCarrier.id) return;

    const awb: AWB = {
      id: shipment.shipmentNumber,
      number: shipment.shipmentNumber,
      carrierWeight: shipment.weight,
      systemWeight: shipment.weight,
      carrierCharges: shipment.amount,
      systemCharges: shipment.amount,
      date: shipment.createdAt,
      origin: shipment.origin,
      destination: shipment.destination,
      currency: shipment.currency,
      selected: false,
      status: 'pending',
    };

    services.push({
      id: shipment.shipmentNumber,
      name: selectedCarrier.name,
      awbs: [awb],
    });
  });

  return services;
};

interface Props {
  selectedCarrier: Carrier | null;
  services: ServiceGroup[];
  setServices: React.Dispatch<React.SetStateAction<ServiceGroup[]>>;
  serviceProviderData: Shipment[];
}

const Step2ServicesAWB: React.FC<Props> = ({ selectedCarrier, services, setServices, serviceProviderData }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [filterSelectValue, setFilterSelectValue] = useState('shipmentNumber');
  const [filterText, setFilterText] = useState('');
  const [dateRange, setDateRange] = useState(initialDateRange);
  const [system, setSystem] = useState<string[]>([]);
  const [shipmentType, setShipmentType] = useState<string[]>([]);

  const SystemList = useSelector((state: RootStateType) => state.System.data.selectedList).map(system => ({
    display: system.name,
    value: system._id,
  }));
  const _systems = useSelector((state: RootStateType) => state.loginUser?.profile?._systems);
  const activeSystemList = useMemo(() => {
    if (_systems?.length) return SystemList.filter(system => _systems.includes(system.value));
    else return [];
  }, [SystemList, _systems]);

  const onDateChange = ({ startDate, endDate }: { startDate: Date; endDate: Date }, isOpen: boolean) => {
    if (isOpen) return;
    const dateRange = { startDate, endDate };
    setDateRange(dateRange);
  };

  const onSystemFilter = (selected: Array<string>) => {
    setSystem(selected);
  };

  const onTypeFilter = (selected: Array<string>) => {
    setShipmentType(selected);
  };

  // Filter functions
  const filterBySearch = (awb: AWB) => {
    if (!filterText) return true;

    const searchValue = filterText.toLowerCase();

    switch (filterSelectValue) {
      case 'shipmentNumber':
        return awb.number.toLowerCase().includes(searchValue);
      case 'origin':
        return awb.origin.toLowerCase().includes(searchValue);
      case 'destination':
        return awb.destination.toLowerCase().includes(searchValue);
      default:
        return true;
    }
  };

  const filterByDateRange = (awb: AWB) => {
    if (!dateRange.startDate || !dateRange.endDate) return true;

    const awbDate = new Date(awb.date);
    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);

    // Set end date to end of day for inclusive range
    endDate.setHours(23, 59, 59, 999);

    return awbDate >= startDate && awbDate <= endDate;
  };

  const filterBySystem = (awb: AWB) => {
    if (system.length === 0) return true;

    // Since AWB doesn't have systemId directly, we need to find the corresponding shipment
    const shipment = serviceProviderData.find(s => s.shipmentNumber === awb.number);
    return shipment && system.includes(shipment.systemId);
  };

  const filterByShipmentType = (awb: AWB) => {
    if (shipmentType.length === 0) return true;

    // Since AWB doesn't have shipmentRouteType directly, we need to find the corresponding shipment
    const shipment = serviceProviderData.find(s => s.shipmentNumber === awb.number);
    return shipment && shipmentType.includes(shipment.shipmentRouteType);
  };

  // Apply all filters
  const filteredAwbs = useMemo(() => {
    const allAwbs = services.flatMap(s => s.awbs);

    return allAwbs.filter(
      awb => filterBySearch(awb) && filterByDateRange(awb) && filterBySystem(awb) && filterByShipmentType(awb),
    );
  }, [services, filterText, filterSelectValue, dateRange, system, shipmentType, serviceProviderData]);

  useEffect(() => {
    if (selectedCarrier && services.length === 0) {
      setLoading(true);
      setTimeout(() => {
        const formattedServices = transformServices(serviceProviderData, selectedCarrier);
        setServices(formattedServices);
        setLoading(false);
      }, 100);
    }
  }, [selectedCarrier, serviceProviderData, services, setServices]);

  const handleAwbCheckbox = (awbId: string, checked: boolean) => {
    setServices(prev =>
      prev.map(s => ({
        ...s,
        awbs: s.awbs.map(a => (a.id === awbId ? { ...a, selected: checked } : a)),
      })),
    );
  };

  const handleSelectAll = (checked: boolean) => {
    // Select only visible (filtered) AWBs
    const visibleAwbIds = new Set(filteredAwbs.map(a => a.id));

    setServices(prev =>
      prev.map(s => ({
        ...s,
        awbs: s.awbs.map(a => (visibleAwbIds.has(a.id) ? { ...a, selected: checked } : a)),
      })),
    );
  };

  const handleClearFilters = () => {
    setFilterText('');
    setDateRange(initialDateRange);
    setSystem([]);
    setShipmentType([]);
  };

  // Check if all visible AWBs are selected
  const allVisibleSelected = filteredAwbs.length > 0 && filteredAwbs.every(a => a.selected);
  const someVisibleSelected = filteredAwbs.some(a => a.selected) && !allVisibleSelected;

  if (!selectedCarrier) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Please select a carrier to view AWB details.
      </Alert>
    );
  }

  const handleBulkMenuClick = (_event: React.MouseEvent<HTMLElement>) =>
    navigate('/purchase-invoices/bulk-creation', {
      state: {
        carrier: selectedCarrier,
        services: services,
      },
    });

  return (
    <Box sx={{ p: 1 }}>
      <TableActionContainer>
        <FilterSearch
          onSelectChange={(value: string) => {
            setFilterSelectValue(value);
          }}
          list={SearchFilterList}
          selectValue={filterSelectValue}
          textValue={filterText}
          onTextChange={e => {
            setFilterText(e.target.value);
          }}
          onTextSearch={() => {
            console.log('Search for:', filterSelectValue, filterText);
          }}
          onTextClear={() => {
            setFilterText('');
          }}
        />
        <DateRangePicker onChange={onDateChange} value={dateRange} />
        <SbButton
          startIcon={<Image src={'/images/file_upload.svg'} alt="upload" />}
          onClick={handleBulkMenuClick}
          sx={{ borderRadius: '8px !important', minWidth: '225px' }}
          variant="outlined"
        >
          Bulk Create New
        </SbButton>
        <FilterSelect id="system" label="System" value={system} options={activeSystemList} onChange={onSystemFilter} />

        <FilterSelect
          id="shipmenttype"
          label="Shipment Type"
          value={shipmentType}
          options={ShipmentTypeList}
          onChange={onTypeFilter}
        />
        <SbButton
          className="xs-text ml-1"
          variant="outlined"
          sx={{ borderRadius: '8px !important', padding: '8px !important' }}
          onClick={handleClearFilters}
        >
          Clear Filters
        </SbButton>
      </TableActionContainer>

      {/* AWB Table */}
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2,
          overflowY: 'auto',
          maxHeight: '450px',
        }}
      >
        <SBTable size="small">
          <TableHead>
            <TableRow
              sx={{
                th: {
                  textAlign: 'center !important',
                },
              }}
            >
              <TableHeadCell align="center">
                <Checkbox
                  checked={allVisibleSelected}
                  indeterminate={someVisibleSelected}
                  onChange={e => handleSelectAll(e.target.checked)}
                  style={{ color: 'inherit' }}
                  sx={{
                    '&.MuiCheckbox-colorPrimary.Mui-disabled': {
                      color: 'grey !important',
                    },
                  }}
                />
              </TableHeadCell>
              <TableHeadCell align="center" sx={{ fontWeight: 'bold' }}>
                Shipment Number
              </TableHeadCell>
              <TableHeadCell align="center" sx={{ fontWeight: 'bold' }}>
                Date
              </TableHeadCell>
              <TableHeadCell align="center" sx={{ fontWeight: 'bold' }}>
                Origin
              </TableHeadCell>
              <TableHeadCell align="center" sx={{ fontWeight: 'bold' }}>
                Destination
              </TableHeadCell>
              <TableHeadCell align="center" sx={{ fontWeight: 'bold' }}>
                Weight (kg)
              </TableHeadCell>
              <TableHeadCell align="center" sx={{ fontWeight: 'bold' }}>
                Charges
              </TableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAwbs.map(a => (
              <SBTableRow
                key={a.id}
                sx={{
                  backgroundColor: a.selected ? '#f0f8ff' : 'inherit',
                  '&:hover': { backgroundColor: a.selected ? '#e3f2fd' : '#fafafa' },
                }}
              >
                <TableRowCell align="center">
                  <Checkbox
                    style={{ color: 'inherit' }}
                    sx={{
                      '&.MuiCheckbox-colorPrimary.Mui-disabled': {
                        color: 'grey !important',
                      },
                    }}
                    checked={a.selected}
                    onChange={e => handleAwbCheckbox(a.id, e.target.checked)}
                  />
                </TableRowCell>
                <TableRowCell align="center" className="md-text uppercase">
                  {a.number}
                </TableRowCell>
                <TableRowCell align="center" className="md-text uppercase">
                  {new Date(a.date).toLocaleDateString()}
                </TableRowCell>
                <TableRowCell align="center" className="md-text uppercase">
                  {addSpaceAfterComma(a.origin)}
                </TableRowCell>
                <TableRowCell align="center" className="md-text uppercase">
                  {addSpaceAfterComma(a.destination)}
                </TableRowCell>
                <TableRowCell align="center" className="md-text uppercase">
                  {a.carrierWeight.toFixed(1)}
                </TableRowCell>
                <TableRowCell align="center" className="md-text uppercase">
                  {a.currency} {a.carrierCharges.toFixed(2)}
                </TableRowCell>
              </SBTableRow>
            ))}
          </TableBody>
        </SBTable>
      </TableContainer>

      {filteredAwbs.length === 0 && !loading && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No AWBs found for the selected filters.
        </Alert>
      )}
    </Box>
  );
};

export default Step2ServicesAWB;
